import React, { useState, useEffect } from 'react';
import { createDeathReport, getDeathReports, getActiveSupportCases } from '../services/firestoreService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import {useAuth} from "../context/AuthContext";

const DeathReports = () => {
    const [showReportForm, setShowReportForm] = useState(false);
    const [activeCases, setActiveCases] = useState([]);
    const [userDeathReports, setUserDeathReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        deceasedName: '',
        relationship: '',
        dateOfPassing: '',
        deathCertificate: null,
        idDocument: null,
        message: ''
    });
    const [uploadProgress, setUploadProgress] = useState({});
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        loadData();
    }, [user]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            const [cases, reports] = await Promise.all([
                getActiveSupportCases(),
                getDeathReports(user.uid)
            ]);
            setActiveCases(cases);
            setUserDeathReports(reports);
        } catch (error) {
            console.error('Error loading data:', error);
            setError('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const validateFile = (file, type) => {
        const errors = {};
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

        if (!file) {
            errors[type] = 'File is required';
            return errors;
        }

        if (file.size > maxSize) {
            errors[type] = 'File size must be less than 5MB';
        }

        if (!allowedTypes.includes(file.type)) {
            errors[type] = 'File must be JPG, PNG, or PDF';
        }

        return errors;
    };

    const handleFileUpload = async (file, type) => {
        const errors = validateFile(file, type);
        if (Object.keys(errors).length > 0) {
            setFormErrors(prev => ({ ...prev, ...errors }));
            return null;
        }

        // Simulate upload progress for UI
        setUploadProgress(prev => ({ ...prev, [type]: 0 }));

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                setUploadProgress(prev => {
                    const newProgress = (prev[type] || 0) + 10;
                    if (newProgress >= 100) {
                        clearInterval(interval);
                        setFormData(prev => ({ ...prev, [type]: file }));
                        setFormErrors(prev => ({ ...prev, [type]: null }));
                        resolve(file);
                        return { ...prev, [type]: 100 };
                    }
                    return { ...prev, [type]: newProgress };
                });
            }, 100);
        });
    };

    const uploadFileToStorage = async (file, path) => {
        try {
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading file:', error);
            throw new Error(`Failed to upload ${path.split('/').pop()}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const errors = {};
        if (!formData.deceasedName.trim()) errors.deceasedName = 'Deceased name is required';
        if (!formData.relationship) errors.relationship = 'Relationship is required';
        if (!formData.dateOfPassing) errors.dateOfPassing = 'Date of passing is required';
        if (!formData.deathCertificate) errors.deathCertificate = 'Death certificate is required';
        if (!formData.idDocument) errors.idDocument = 'ID document is required';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSubmitting(true);
        setFormErrors({});

        try {
            // Upload files to Firebase Storage
            const deathCertUrl = await uploadFileToStorage(
                formData.deathCertificate,
                `death-reports/${user.uid}/${Date.now()}-death-certificate`
            );

            const idDocUrl = await uploadFileToStorage(
                formData.idDocument,
                `death-reports/${user.uid}/${Date.now()}-id-document`
            );

            // Create death report in Firestore
            const reportData = {
                reportedBy: user.uid,
                reportedByName: user.name,
                deceasedName: formData.deceasedName.trim(),
                relationship: formData.relationship,
                dateOfPassing: formData.dateOfPassing,
                message: formData.message.trim(),
                deathCertificateUrl: deathCertUrl,
                idDocumentUrl: idDocUrl,
                status: 'pending_review',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await createDeathReport(reportData);

            // Success
            alert('Death report submitted successfully! It will be reviewed within 24-48 hours.');
            setShowReportForm(false);
            resetForm();
            loadData(); // Reload data to show the new report
        } catch (error) {
            console.error('Error submitting death report:', error);
            setFormErrors({ submit: error.message || 'Failed to submit report. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            deceasedName: '',
            relationship: '',
            dateOfPassing: '',
            deathCertificate: null,
            idDocument: null,
            message: ''
        });
        setFormErrors({});
        setUploadProgress({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const FileUploadField = ({ label, name, accept, required = false }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                    type="file"
                    name={name}
                    accept={accept}
                    onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                            await handleFileUpload(file, name);
                        }
                    }}
                    className="hidden"
                    id={name}
                />
                <label htmlFor={name} className="cursor-pointer">
                    {formData[name] ? (
                        <div className="space-y-2">
                            <div className="text-green-600 text-lg">✅</div>
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {formData[name].name}
                            </p>
                            <p className="text-xs text-gray-500">
                                {Math.round(formData[name].size / 1024)} KB • Click to change file
                            </p>
                            {uploadProgress[name] !== undefined && uploadProgress[name] < 100 && (
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress[name]}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="text-gray-400 text-lg">📄</div>
                            <p className="text-sm font-medium text-gray-900">
                                Click to upload {label.toLowerCase()}
                            </p>
                            <p className="text-xs text-gray-500">
                                Supports JPG, PNG, PDF (Max 5MB)
                            </p>
                        </div>
                    )}
                </label>
            </div>
            {formErrors[name] && (
                <p className="text-red-500 text-sm mt-1">{formErrors[name]}</p>
            )}
        </div>
    );

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending_review: { color: 'yellow', text: 'Under Review', icon: '⏳' },
            approved: { color: 'green', text: 'Approved', icon: '✅' },
            rejected: { color: 'red', text: 'Rejected', icon: '❌' },
            needs_more_info: { color: 'orange', text: 'More Info Needed', icon: 'ℹ️' }
        };

        const config = statusConfig[status] || statusConfig.pending_review;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <span className="mr-1">{config.icon}</span>
                {config.text}
      </span>
        );
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="p-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="animate-pulse border border-gray-200 rounded-lg p-6 mb-4">
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadData}
                            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Report Death Button */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Report a Loss</h3>
                        <p className="text-gray-600">
                            Submit a death report with required documents for community support
                        </p>
                    </div>
                    <button
                        onClick={() => setShowReportForm(true)}
                        className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                    >
                        Submit Death Report
                    </button>
                </div>
            </div>

            {/* My Death Reports */}
            {userDeathReports.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">My Death Reports</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Track the status of your submitted reports
                        </p>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            {userDeathReports.map((report) => (
                                <div key={report.id} className="border border-gray-200 rounded-lg p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h4 className="font-semibold text-gray-900">{report.deceasedName}</h4>
                                                {getStatusBadge(report.status)}
                                            </div>
                                            <p className="text-gray-600">Relationship: {report.relationship}</p>
                                            <p className="text-sm text-gray-500">
                                                Date of Passing: {formatDate(report.dateOfPassing)} •
                                                Submitted: {formatDate(report.createdAt)}
                                            </p>
                                            {report.message && (
                                                <p className="mt-2 text-gray-700 bg-gray-50 p-3 rounded-lg">
                                                    {report.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex space-x-4">
                                            {report.deathCertificateUrl && (
                                                <a
                                                    href={report.deathCertificateUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:text-primary-800 font-medium"
                                                >
                                                    View Death Certificate
                                                </a>
                                            )}
                                            {report.idDocumentUrl && (
                                                <a
                                                    href={report.idDocumentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:text-primary-800 font-medium"
                                                >
                                                    View ID Document
                                                </a>
                                            )}
                                        </div>
                                        <button className="text-gray-600 hover:text-gray-800 text-sm font-medium">
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Active Support Cases */}
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Active Support Cases
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Community members currently receiving support
                    </p>
                </div>
                <div className="p-6">
                    {activeCases.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400 text-4xl mb-4">🕊️</div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">No Active Support Cases</h4>
                            <p className="text-gray-600">
                                There are currently no active support cases requiring community assistance.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {activeCases.map((caseItem) => (
                                <div key={caseItem.id} className="border border-gray-200 rounded-lg p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h4 className="font-semibold text-gray-900 text-lg">
                                                    {caseItem.memberName || 'Community Member'}
                                                </h4>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    caseItem.status === 'approved'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                          {caseItem.status === 'approved' ? 'Verified' : 'Under Review'}
                        </span>
                                            </div>
                                            <p className="text-gray-600">Loss of {caseItem.relationship}</p>
                                            <p className="text-sm text-gray-500">
                                                Reported on {formatDate(caseItem.createdAt)}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-primary-600">
                                                ${caseItem.amountRaised || 0}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                raised of ${caseItem.amountNeeded || 1000}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="mb-4">
                                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                                            <span>Community Support Progress</span>
                                            <span>{(caseItem.contributors || []).length} members contributed</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(((caseItem.amountRaised || 0) / (caseItem.amountNeeded || 1000)) * 100, 100)}%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <button className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors font-medium">
                                        Contribute $50 to Support
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Report Form Modal */}
            {showReportForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Submit Death Report
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Please provide all required documents for verification
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Deceased Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="deceasedName"
                                        value={formData.deceasedName}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            formErrors.deceasedName ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter full legal name"
                                    />
                                    {formErrors.deceasedName && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.deceasedName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Relationship to Member <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="relationship"
                                        value={formData.relationship}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                            formErrors.relationship ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    >
                                        <option value="">Select relationship</option>
                                        <option value="self">Self (Member)</option>
                                        <option value="spouse">Spouse</option>
                                        <option value="child">Child</option>
                                        <option value="parent">Parent</option>
                                        <option value="sibling">Sibling</option>
                                        <option value="other">Other Relative</option>
                                    </select>
                                    {formErrors.relationship && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.relationship}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date of Passing <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="dateOfPassing"
                                    value={formData.dateOfPassing}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                        formErrors.dateOfPassing ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {formErrors.dateOfPassing && (
                                    <p className="text-red-500 text-sm mt-1">{formErrors.dateOfPassing}</p>
                                )}
                            </div>

                            {/* Document Uploads */}
                            <div className="space-y-6">
                                <h4 className="font-medium text-gray-900 border-b pb-2">
                                    Required Documents
                                </h4>

                                <FileUploadField
                                    label="Death Certificate"
                                    name="deathCertificate"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    required
                                />

                                <FileUploadField
                                    label="Your ID Document (Government Issued)"
                                    name="idDocument"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    required
                                />

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h5 className="font-medium text-blue-900 mb-2">📋 Document Requirements</h5>
                                    <ul className="text-sm text-blue-800 space-y-1">
                                        <li>• Death certificate must be official and clearly readable</li>
                                        <li>• ID document must be government-issued (Driver's License, Passport, etc.)</li>
                                        <li>• Files must be in JPG, PNG, or PDF format</li>
                                        <li>• Maximum file size: 5MB per document</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Additional Information */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Message to Community (Optional)
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="Share a message with the community or provide any additional information..."
                                />
                            </div>

                            {/* Submit Error */}
                            {formErrors.submit && (
                                <div className="rounded-md bg-red-50 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800">
                                                {formErrors.submit}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReportForm(false);
                                        resetForm();
                                    }}
                                    disabled={submitting}
                                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || !formData.deathCertificate || !formData.idDocument}
                                    className="flex-1 bg-primary-500 text-white px-4 py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center"
                                >
                                    {submitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Report for Review'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeathReports;